import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import SVG from 'components/renderSvg';
import escape from 'media/svgs/escape.svg';
import { Link, useNavigate } from 'react-router-dom';
// No need for subscription plans import as we handle plan types directly
import useStripeSubscription from '../../hooks/useStripeSubscription';
// Firebase auth removed - using token-based auth
import logo from 'media/pngs/logo.png';
import edit from 'media/svgs/edit.svg';
import useVendor from '../../hooks/useVendor';
import useBatch from '../../hooks/useBatch';
import toast from 'react-hot-toast';
import { useEffect } from 'react';

const Plans = () => {
  const [billing, setBilling] = useState(false); // false for monthly, true for yearly
  const navigate = useNavigate();
  const { venderData } = useSelector(state => state.vender);
  const { handleSubscribe, openCustomerPortal, handleCancelSubscription, loading } = useStripeSubscription();
  const [serviceCount, setServiceCount] = useState(0);
  const [batchCount, setBatchCount] = useState(0);
  const { fetchServicesByVendor } = useVendor();
  const { fetchBatchesByService } = useBatch();

  useEffect(() => {
    const fetchUsage = async () => {
      const sResp = await fetchServicesByVendor();
      if (sResp.success) setServiceCount(sResp.data?.services?.length || sResp.data?.length || 0);

      const bResp = await fetchBatchesByService();
      if (bResp.success) setBatchCount(bResp.data?.length || 0);
    };
    fetchUsage();
  }, [fetchServicesByVendor, fetchBatchesByService]);

  const stripe_price_ids = {
    PRO: {
      monthly: process.env.REACT_APP_STRIPE_PRO_MONTHLY_PRICE_ID,
      yearly: process.env.REACT_APP_STRIPE_PRO_YEARLY_PRICE_ID
    }
  };

  const handleUpgrade = async () => {
    const priceId = stripe_price_ids.PRO[billing ? 'yearly' : 'monthly'];
    if (!priceId) {
      toast.error('Price ID not configured');
      return;
    }
    await handleSubscribe(priceId, 'PRO', billing ? 'yearly' : 'monthly');
  };

  const handleCancel = async () => {
    const confirmed = window.confirm('Are you sure you want to cancel your subscription? You will lose access to Pro features.');
    if (!confirmed) return;

    // Using subscription_id or id from venderData
    const subscriptionId = venderData?.subscription_id || venderData?.id;
    await handleCancelSubscription(subscriptionId);
  };

  const handleDeleteAccount = async () => {
    const confirmed = window.confirm('Are you sure you want to permanently delete your account? This action cannot be undone.');
    if (!confirmed) return;
    try {
      // TODO: Implement account deletion via backend API
      // This would call: DELETE /api/v1/vendor/profile
      console.warn('Account deletion not yet implemented - needs backend API endpoint');
      toast.error('Account deletion not yet available. Please contact support.');
      // navigate('/');
    } catch (e) {
      // Most common case: requires recent login
      alert('Unable to delete account. Please sign in again, then try deleting your account.');
    }
  };

  if (loading) {
    return (
      <div className='w-full flex justify-center items-center min-h-[400px]'>
        <div className='p-8 text-gray-600'>Loading...</div>
      </div>
    );
  }

  const now = Date.now();
  const trialEndsSource = venderData?.trialEndsAtMillis || venderData?.trial_ends_at || venderData?.trial_end;
  let trialEnds = 0;
  if (trialEndsSource) {
    if (typeof trialEndsSource === 'number') {
      trialEnds = trialEndsSource;
    } else {
      const trialDate = new Date(trialEndsSource).getTime();
      if (trialDate > 0 && trialDate > new Date('1970-01-01').getTime()) {
        trialEnds = trialDate;
      }
    }
  }
  const status = venderData?.subscription_tier || venderData?.status || 'free';
  const isSubscribed = status === 'pro' || status === 'active';
  // Trial is active if we have a future end date, regardless of status (unless subscribed)
  const isTrialActive = !isSubscribed && trialEnds > 0 && now <= trialEnds;

  const selectedPlan = isSubscribed ? 'PRO' : 'FREE';

  const UsageBar = ({ label, current, total, colorClass }) => (
    <div className="mb-4">
      <div className="flex justify-between items-center mb-1.5">
        <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">{label}</span>
        <span className="text-xs font-bold text-gray-900">{current} / {total}</span>
      </div>
      <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-full transition-all duration-500 ${colorClass}`}
          style={{ width: `${Math.min(100, (current / total) * 100)}%` }}
        />
      </div>
    </div>
  );

  return (
    <div className='w-full min-h-screen bg-[#fafafa]'>
      {/* Header */}
      <div className='p-6 flex items-center justify-between sticky top-0 bg-white/70 backdrop-blur-md z-30 border-b border-gray-100'>
        <div className='flex items-center space-x-6'>
          <button
            className='flex items-center space-x-2 px-4 py-2 bg-white border border-gray-200 rounded-full shadow-sm hover:shadow-md transition-all hover:-translate-x-1 active:scale-95'
            onClick={() => navigate(-1)}
          >
            <SVG icon={escape} className='size-5' />
            <span className='font-medium text-dark'>Back</span>
          </button>

          <div className='flex gap-3'>
            {!isSubscribed && !isTrialActive && (
              <span className='flex items-center gap-2 px-4 py-1.5 bg-red-50 text-red-600 border border-red-100 rounded-full text-xs font-bold uppercase tracking-wider animate-pulse'>
                Trial Expired
              </span>
            )}
            {isSubscribed && (
              <span className='flex items-center gap-2 px-4 py-1.5 bg-green-50 text-green-600 border border-green-100 rounded-full text-xs font-bold uppercase tracking-wider'>
                <div className='size-2 bg-green-500 rounded-full animate-ping'></div>
                Pro Active
              </span>
            )}
          </div>
        </div>

        <div className='flex items-center space-x-6'>
          <Link to='/setup' className='flex items-center space-x-2 p-2 hover:bg-gray-100 rounded-lg transition-colors group'>
            <SVG icon={edit} className='size-5 text-gray-400 group-hover:text-primary' />
            <span className='text-sm font-semibold text-gray-500 group-hover:text-primary transition-colors'>Settings</span>
          </Link>
        </div>
      </div>

      {/* Logo and Title Section */}
      <div className='relative overflow-hidden pt-10 pb-8 px-4'>
        <div className='absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-primary/5 blur-[120px] rounded-full -z-10'></div>
        <div className='flex flex-col items-center justify-center'>
          <div className='relative group'>
            <div className='absolute -inset-1 bg-gradient-to-r from-primary to-indigo-600 rounded-full blur opacity-25 group-hover:opacity-50 transition duration-1000 group-hover:duration-200'></div>
            <div className='relative w-20 h-20 bg-white shadow-xl rounded-full flex items-center justify-center mb-6 overflow-hidden border border-gray-100'>
              <img src={logo} alt='logo' className='w-12 h-auto' />
            </div>
          </div>
          <h1 className='text-4xl md:text-5xl font-black text-dark mb-4 tracking-tighter'>
            Sportona® <span className='bg-clip-text text-transparent bg-gradient-to-r from-primary to-indigo-600'>Membership</span>
          </h1>
          <p className='text-gray-500 text-center max-w-xl text-lg leading-relaxed'>
            Choose the subscription plan that suits your business perfectly.
            <span className='hidden sm:inline'> Switch plans anytime with zero friction.</span>
          </p>
        </div>
      </div>

      {/* Usage Overview for Free/Trial */}
      {!isSubscribed && (
        <div className="max-w-4xl mx-auto px-4 mb-10">
          <div className="bg-white rounded-[32px] p-8 shadow-sm border border-gray-100">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-8">
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-1">Usage Overview</h3>
                <p className="text-sm text-gray-500">Track your current usage on the {isTrialActive ? 'Trial' : 'Free'} Plan.</p>
              </div>
              <div className="flex-1 max-w-md w-full">
                <UsageBar
                  label="Services"
                  current={serviceCount}
                  total={1}
                  colorClass="bg-brand-500"
                />
                <UsageBar
                  label="Batches"
                  current={batchCount}
                  total={2}
                  colorClass="bg-indigo-500"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Billing Toggle */}
      {/* <div className='flex justify-center items-center space-x-6 mb-10'>
        <span className={`text-sm font-bold transition-colors ${!billing ? 'text-dark' : 'text-gray-400'}`}>Monthly</span>
        <button
          className={`relative w-16 h-8 rounded-full p-1 transition-colors duration-300 focus:outline-none ${billing ? 'bg-primary shadow-inner shadow-black/20' : 'bg-gray-200 shadow-inner'}`}
          onClick={() => setBilling(!billing)}
        >
          <div
            className={`bg-white w-6 h-6 rounded-full shadow-lg transform transition-transform duration-300 ease-in-out ${billing ? 'translate-x-8' : 'translate-x-0'}`}
          />
        </button>
        <div className='flex items-center space-x-3'>
          <span className={`text-sm font-bold transition-colors ${billing ? 'text-dark' : 'text-gray-400'}`}>Yearly</span>
          <span className='bg-indigo-600 text-white text-[10px] sm:text-xs px-3 py-1 rounded-full font-black tracking-widest uppercase shadow-lg shadow-indigo-200'>
            Save 16.67%
          </span>
        </div>
      </div> */}

      {/* Pricing Plans */}
      <div className='max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-12'>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-8 items-center'>
          {/* Free Plan */}
          <div className={`relative group p-1 rounded-3xl transition-all duration-500 ${selectedPlan === 'FREE' ? 'bg-gradient-to-b from-gray-200 to-transparent' : 'bg-transparent'}`}>
            <div className={`h-full rounded-[20px] p-8 transition-all duration-500 ${selectedPlan === 'FREE' ? 'bg-white shadow-2xl scale-[1.02]' : 'bg-white/50 backdrop-blur-sm border border-gray-200 hover:border-gray-300'}`}>
              <div className='mb-8'>
                <h3 className='text-xl font-bold text-dark mb-1'>Starter</h3>
                <div className='flex items-baseline gap-1 mb-4'>
                  <span className='text-3xl font-black text-dark'>Free</span>
                  <span className='text-gray-400 text-sm font-medium'>/ {isTrialActive ? 'Trial Active' : 'Basic Access'}</span>
                </div>
                <p className='text-sm text-gray-500 leading-relaxed'>Perfect for new businesses looking to build their first digital presence.</p>
              </div>

              <div className='space-y-4 mb-8'>
                {[
                  'One Service limit',
                  'Two Batches limit',
                  'Manage business info',
                  'Standard support'
                ].map((feature) => (
                  <div key={feature} className='flex items-center gap-3 group/item'>
                    <div className='size-5 rounded-full bg-green-50 flex items-center justify-center shrink-0 group-hover/item:scale-110 transition-transform'>
                      <svg className='h-3 w-3 text-green-600' fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth='3' d='M5 13l4 4L19 7' /></svg>
                    </div>
                    <span className='text-gray-600 text-sm font-medium'>{feature}</span>
                  </div>
                ))}
              </div>

              {!isSubscribed ? (
                <button className='w-full py-4 px-6 rounded-2xl bg-gray-50 text-gray-400 font-bold border border-gray-100 cursor-default'>
                  Current Plan
                </button>
              ) : (
                <button onClick={handleCancel} className='w-full py-4 px-6 rounded-2xl bg-white text-dark font-bold border border-gray-200 hover:bg-gray-50 transition-all active:scale-95'>
                  Downgrade Plan
                </button>
              )}
            </div>
          </div>

          {/* Pro Plan */}
          <div className={`relative group p-1 rounded-3xl transition-all duration-500 ${selectedPlan === 'PRO' ? 'bg-gradient-to-br from-primary via-indigo-600 to-purple-600 shadow-2xl shadow-indigo-200' : 'bg-gray-200 hover:bg-gray-300 scale-95 opaciy-90'}`}>
            <div className='absolute -top-4 left-1/2 transform -translate-x-1/2 z-10'>
              <span className='bg-primary text-white text-[10px] font-black tracking-widest px-4 py-1.5 rounded-full shadow-xl border border-white/10 uppercase bg-gradient-to-r from-primary to-indigo-900'>
                Recommended
              </span>
            </div>

            <div className={`h-full rounded-[20px] p-8 transition-all duration-500 ${selectedPlan === 'PRO' ? 'bg-white' : 'bg-white/80 backdrop-blur-md'}`}>
              <div className='mb-8 text-center sm:text-left'>
                <h3 className='text-2xl font-black text-dark mb-1'>Pro</h3>
                <div className='flex items-baseline justify-center sm:justify-start gap-1 mb-4'>
                  <span className='text-5xl font-black text-dark tracking-tighter'>
                    {billing ? '£400' : '£40'}
                  </span>
                  <span className='text-gray-400 text-lg font-medium'>/month</span>
                </div>
                <p className='text-sm text-gray-500 leading-relaxed'>Serious businesses looking to build a massive stream of subscriptions.</p>
              </div>

              <div className='space-y-4 mb-10'>
                {[
                  'Unlimited Services',
                  'Unlimited Batches',
                  'Advanced analytics',
                  'Priority 24/7 support',
                ].map((feature) => (
                  <div key={feature} className='flex items-center gap-3 group/item'>
                    <div className='size-5 rounded-full bg-primary/5 flex items-center justify-center shrink-0 group-hover/item:scale-110 transition-transform'>
                      <svg className='h-3 w-3 text-primary' fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth='3' d='M5 13l4 4L19 7' /></svg>
                    </div>
                    <span className='text-dark text-sm font-bold'>{feature}</span>
                  </div>
                ))}
              </div>

              {isSubscribed ? (
                <button onClick={openCustomerPortal} className='w-full py-4 px-6 rounded-2xl bg-indigo-50 text-indigo-700 font-black hover:bg-indigo-100 transition-all active:scale-95 shadow-lg shadow-indigo-100/50'>
                  Managing Subscription
                </button>
              ) : (
                <button
                  onClick={handleUpgrade}
                  disabled={loading}
                  className='w-full py-4 px-6 rounded-2xl bg-primary text-white font-black hover:shadow-2xl hover:shadow-primary/40 transition-all hover:-translate-y-1 active:scale-95 flex items-center justify-center gap-2 group/btn'
                >
                  {loading ? 'Processing...' : 'Upgrade Now'}
                  {!loading && <svg className='w-4 h-4 transition-transform group-hover/btn:translate-x-1' fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth='3' d='M13 7l5 5m0 0l-5 5m5-5H6' /></svg>}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Manage / Cancel subscription and delete account */}
        <div className='mt-8 pb-12 flex flex-col items-center space-y-6'>
          {isSubscribed && (
            <div className='flex flex-col items-center gap-4'>
              {/* <button onClick={openCustomerPortal} className='text-sm font-bold text-indigo-600 hover:text-indigo-500 transition-colors flex items-center gap-2 group'>
                Manage your subscription
                <span className='group-hover:translate-x-1 transition-transform'>→</span>
              </button> */}
              <button
                onClick={handleCancel}
                className='px-6 py-2 rounded-full border border-red-200 text-red-500 text-sm font-bold hover:bg-red-50 hover:border-red-300 transition-all active:scale-95'
              >
                Cancel my subscription
              </button>
            </div>
          )}
          <div className='h-px w-20 bg-gray-100'></div>
          <button onClick={handleDeleteAccount} className='text-xs font-bold text-red-500 hover:text-red-600 transition-colors uppercase tracking-widest opacity-60 hover:opacity-100'>
            Permanently delete my account
          </button>
        </div>
      </div>
    </div>
  );
};

export default Plans;